// 🔐 Enhanced JWT Token Management Service
// Features: Auto-logout, Session Management, Single-Device Login, Token Refresh

import { toast } from "react-toastify";

class JWTTokenService {
  constructor() {
    // Storage Keys
    this.TOKEN_KEY = "trivana_jwt_token";
    this.REFRESH_TOKEN_KEY = "trivana_refresh_token";
    this.USER_KEY = "trivana_user";
    this.TOKEN_EXPIRY_KEY = "trivana_token_expiry";
    this.LAST_ACTIVITY_KEY = "trivana_last_activity";
    this.DEVICE_ID_KEY = "trivana_device_id";
    this.SESSION_ID_KEY = "trivana_session_id";
    this.LOGIN_TIMESTAMP_KEY = "trivana_login_timestamp";

    // Time Configurations (in milliseconds)
    this.TOKEN_EXPIRY_DAYS = 7; // Token expires after 7 days
    this.INACTIVITY_DAYS = 7; // Auto-logout after 7 days of inactivity
    this.WARNING_BEFORE_LOGOUT = 24 * 60 * 60 * 1000; // Warn 24 hours before logout
    this.ACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

    // Convert to milliseconds
    this.TOKEN_EXPIRY_MS = this.TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    this.INACTIVITY_MS = this.INACTIVITY_DAYS * 24 * 60 * 60 * 1000;

    // Timers
    this.activityCheckTimer = null;
    this.warningTimer = null;

    // Bind methods
    this.handleUserActivity = this.handleUserActivity.bind(this);
    this.checkInactivity = this.checkInactivity.bind(this);

    // Initialize
    this.initializeService();
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  initializeService() {
    // Generate or retrieve device ID
    this.initializeDeviceId();

    // Track user activity
    this.initializeActivityTracking();

    // Start periodic inactivity checks
    this.startActivityMonitoring();

    // Validate session on load
    this.validateSessionOnLoad();
  }

  initializeDeviceId() {
    let deviceId = localStorage.getItem(this.DEVICE_ID_KEY);

    if (!deviceId) {
      // Generate unique device ID
      deviceId = this.generateDeviceId();
      localStorage.setItem(this.DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  }

  generateDeviceId() {
    // Create a unique device fingerprint
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Device ID", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Device ID", 4, 17);

    const canvasData = canvas.toDataURL();

    // Combine with other browser data
    const fingerprint = [
      canvasData,
      navigator.userAgent,
      navigator.language,
      window.screen.colorDepth,
      window.screen.width + "x" + window.screen.height,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency,
      navigator.deviceMemory,
    ].join("|");

    // Generate hash
    return this.hashString(fingerprint);
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  initializeActivityTracking() {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "focus",
    ];

    events.forEach((event) => {
      document.addEventListener(event, this.handleUserActivity, {
        passive: true,
      });
    });
  }

  startActivityMonitoring() {
    // Check activity every 5 minutes
    this.activityCheckTimer = setInterval(
      this.checkInactivity,
      this.ACTIVITY_CHECK_INTERVAL
    );
  }

  validateSessionOnLoad() {
    if (this.isAuthenticated()) {
      // Check if token expired
      if (this.isTokenExpired()) {
        this.handleTokenExpired();
        return;
      }

      // Check if inactive for too long
      if (this.isInactive()) {
        this.handleInactivityLogout();
        return;
      }

      // Session is valid, update last activity
      this.updateLastActivity();
    }
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  setToken(token, expiresIn = null) {
    const expirationTime = expiresIn
      ? Date.now() + expiresIn * 1000
      : Date.now() + this.TOKEN_EXPIRY_MS;

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expirationTime.toString());
    localStorage.setItem(this.LOGIN_TIMESTAMP_KEY, Date.now().toString());

    this.updateLastActivity();
    this.generateSessionId();
  }

  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  setRefreshToken(refreshToken) {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  getRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isTokenExpired() {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiry) return true;

    return Date.now() > parseInt(expiry);
  }

  getTokenExpiryTime() {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry) : null;
  }

  getTimeUntilExpiry() {
    const expiryTime = this.getTokenExpiryTime();
    if (!expiryTime) return 0;

    const timeLeft = expiryTime - Date.now();
    return Math.max(0, timeLeft);
  }

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  generateSessionId() {
    const sessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    localStorage.setItem(this.SESSION_ID_KEY, sessionId);
    return sessionId;
  }

  getSessionId() {
    return localStorage.getItem(this.SESSION_ID_KEY);
  }

  getDeviceId() {
    return localStorage.getItem(this.DEVICE_ID_KEY);
  }

  // ============================================
  // ACTIVITY TRACKING
  // ============================================

  handleUserActivity() {
    if (this.isAuthenticated()) {
      this.updateLastActivity();
    }
  }

  updateLastActivity() {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
  }

  getLastActivity() {
    const lastActivity = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    return lastActivity ? parseInt(lastActivity) : null;
  }

  getTimeSinceLastActivity() {
    const lastActivity = this.getLastActivity();
    if (!lastActivity) return Infinity;

    return Date.now() - lastActivity;
  }

  isInactive() {
    return this.getTimeSinceLastActivity() > this.INACTIVITY_MS;
  }

  getTimeUntilInactiveLogout() {
    const timeSinceActivity = this.getTimeSinceLastActivity();
    const timeLeft = this.INACTIVITY_MS - timeSinceActivity;
    return Math.max(0, timeLeft);
  }

  checkInactivity() {
    if (!this.isAuthenticated()) return;

    const timeUntilLogout = this.getTimeUntilInactiveLogout();

    // Show warning if close to logout
    if (
      timeUntilLogout > 0 &&
      timeUntilLogout <= this.WARNING_BEFORE_LOGOUT &&
      !this.warningShown
    ) {
      this.showInactivityWarning(timeUntilLogout);
      this.warningShown = true;
    }

    // Logout if inactive too long
    if (this.isInactive()) {
      this.handleInactivityLogout();
    }
  }

  showInactivityWarning(timeLeft) {
    const daysLeft = Math.ceil(timeLeft / (24 * 60 * 60 * 1000));
    const hoursLeft = Math.ceil(timeLeft / (60 * 60 * 1000));

    const extendSession = () => {
      this.updateLastActivity();
      this.warningShown = false;
      toast.dismiss("inactivity-warning");
      toast.success("Session extended successfully!");
    };

    const message =
      daysLeft > 1
        ? `You'll be logged out in ${daysLeft} days due to inactivity.`
        : `You'll be logged out in ${hoursLeft} hours due to inactivity.`;

    toast.warn(
      <div className="space-y-2">
        <p className="text-sm font-semibold">{message}</p>
        <button
          onClick={extendSession}
          className="w-full px-4 py-2 bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white rounded-lg font-bold text-sm hover:shadow-lg transition-all"
        >
          Continue Session
        </button>
      </div>,
      {
        toastId: "inactivity-warning",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        className: "bg-white rounded-xl shadow-xl",
      }
    );
  }

  handleInactivityLogout() {
    toast.dismiss("inactivity-warning");
    this.logout();
    toast.error(
      `You have been logged out after ${this.INACTIVITY_DAYS} days of inactivity.`
    );

    // Redirect to login
    setTimeout(() => {
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }, 1000);
  }

  handleTokenExpired() {
    this.logout();
    toast.error("Your session has expired. Please login again.");

    setTimeout(() => {
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }, 1000);
  }

  // ============================================
  // USER DATA MANAGEMENT
  // ============================================

  setUser(userData) {
    localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
  }

  getUser() {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  // ============================================
  // AUTHENTICATION CHECKS
  // ============================================

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    // Check if token expired
    if (this.isTokenExpired()) return false;

    // Check if inactive
    if (this.isInactive()) return false;

    return true;
  }

  requireAuth() {
    if (!this.isAuthenticated()) {
      toast.error("Please login to continue");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return false;
    }
    return true;
  }

  // ============================================
  // AUTHORIZATION HEADERS
  // ============================================

  getAuthHeader() {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  getAuthHeaders() {
    const token = this.getToken();
    const deviceId = this.getDeviceId();
    const sessionId = this.getSessionId();

    return {
      Authorization: token ? `Bearer ${token}` : "",
      "X-Device-ID": deviceId || "",
      "X-Session-ID": sessionId || "",
      "Content-Type": "application/json",
    };
  }

  // ============================================
  // LOGOUT
  // ============================================

  logout(reason = "User logged out") {
    // Clear all timers
    if (this.activityCheckTimer) {
      clearInterval(this.activityCheckTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    // Clear all storage except device ID (keep for device tracking)
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
    localStorage.removeItem(this.SESSION_ID_KEY);
    localStorage.removeItem(this.LOGIN_TIMESTAMP_KEY);

    // Clear warning flag
    this.warningShown = false;

    // Dispatch logout event
    window.dispatchEvent(new CustomEvent("logout", { detail: { reason } }));
  }

  // ============================================
  // SESSION INFO
  // ============================================

  getSessionInfo() {
    return {
      isAuthenticated: this.isAuthenticated(),
      user: this.getUser(),
      deviceId: this.getDeviceId(),
      sessionId: this.getSessionId(),
      tokenExpiresIn: this.getTimeUntilExpiry(),
      lastActivity: this.getLastActivity(),
      timeSinceActivity: this.getTimeSinceLastActivity(),
      timeUntilInactiveLogout: this.getTimeUntilInactiveLogout(),
      loginTimestamp: localStorage.getItem(this.LOGIN_TIMESTAMP_KEY),
    };
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  formatTime(milliseconds) {
    const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
    const hours = Math.floor(
      (milliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    );
    const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  // ============================================
  // CLEANUP
  // ============================================

  cleanup() {
    // Remove event listeners
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "focus",
    ];

    events.forEach((event) => {
      document.removeEventListener(event, this.handleUserActivity);
    });

    // Clear timers
    if (this.activityCheckTimer) clearInterval(this.activityCheckTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
  }
}

// Create singleton instance
const jwtTokenService = new JWTTokenService();

// Export singleton
export default jwtTokenService;

// Export individual methods for convenience
export const {
  setToken,
  getToken,
  setRefreshToken,
  getRefreshToken,
  setUser,
  getUser,
  isAuthenticated,
  requireAuth,
  getAuthHeader,
  getAuthHeaders,
  logout,
  getSessionInfo,
  getDeviceId,
  getSessionId,
  isTokenExpired,
  getTimeUntilExpiry,
  getTimeUntilInactiveLogout,
  formatTime,
} = jwtTokenService;
